import { Card, CardTypeMap, Grid, SxProps, Theme, Tooltip, Typography, styled } from "@mui/material";
import { DateTime } from "luxon";
import React, { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import { Defined } from "sonobello.utilities.react";

import { SessionContext } from "../../Context/SessionContext";
import { BlockType } from "../../Models/Block";
import { Customer } from "../../Models/Customer";
import { EnhancedOpsSlot } from "../../Models/OpsSlot";
import { Service } from "../../Models/Service";
import useResilientCreateSlotReservation from "../../V2/Playbook/Booking/Compositions/useResilientCreateSlotReservation";

type TimeItemSelectTypes = BlockType | "selected" | "trySelected";

const titles: Record<TimeItemSelectTypes, string> = {
  [BlockType.Booked]: "Booked Appointment",
  [BlockType.Blocked]: "Blocked",
  [BlockType.Reserved]: "Reserved",
  [BlockType.Open]: "",
  selected: "",
  trySelected: ""
};

interface Props {
  /** Flag indicating that this component should block user IO. */
  disabled?: boolean;
  /** Flag indicating if auto-selection of the earliest time should be allowed. */
  preventAutoSelect: boolean;
  /** The service which is attempting to be booked. */
  service?: Pick<Service, "id" | "duration">;
  /** The currently selected slot. */
  selectedSlot?: EnhancedOpsSlot;
  /** The range of Blocks to be displayed for selection and the selection state for each. */
  timeslots: EnhancedOpsSlot[];
  /** Callback to be executed when a time and slot has been successfully reserved. */
  setSelectedSlot: Dispatch<SetStateAction<EnhancedOpsSlot | undefined>>;
}

/** A list of times for a given day which the user can select and which confirms reservation success prior to updating the state. */
const BookingWizardTimeSelector: React.FC<Props> = ({
  disabled = false,
  preventAutoSelect,
  service,
  selectedSlot,
  timeslots,
  setSelectedSlot
}: Props) => {
  const { lead, customer } = useContext(SessionContext);
  const [trySelectedSlot, setTrySelectedSlot] = useState<EnhancedOpsSlot>();
  const slotRef = useRef<EnhancedOpsSlot>();
  const listRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

  // ON START TIME CHANGE
  useEffect(() => {
    // if unset then clear the selected state
    if (!selectedSlot) {
      setTrySelectedSlot(undefined);
    }
  }, [selectedSlot]);

  // ON TIMESLOTS CHANGE
  useEffect(() => {
    // create a ref for each timeslot item
    const refs: Record<string, React.RefObject<HTMLDivElement>> = {};
    timeslots.map(({ startTime }) => (refs[startTime.toISO()] = React.createRef<HTMLDivElement>()));
    listRefs.current = refs;

    // Return if preventAutoSelect is true.
    if (preventAutoSelect) return;

    // try to pick the first available appointment, if any
    const timeslotCheck =
      !slotRef.current || !timeslots.some(ts => slotRef.current && ts.startTime.equals(slotRef.current.startTime));

    if (timeslotCheck) {
      const paddedBoundary = DateTime.utc().plus({
        minutes: Number(process.env.REACT_APP_TIMESLOT_PADDING_MINUTES || 5)
      });
      const slot = timeslots.find(block => block.type === BlockType.Open && block.startTime > paddedBoundary);
      slotRef.current = slot;
      setTrySelectedSlot(slot);
      if (!slot) setSelectedSlot(undefined);
    }
  }, [preventAutoSelect, timeslots, setSelectedSlot]);

  // ON SELECTED TIMESLOT CHANGE, SCROLL TO IT
  useEffect(() => {
    if (trySelectedSlot?.startTime && listRefs.current[trySelectedSlot.startTime.toISO()]?.current) {
      listRefs.current[trySelectedSlot.startTime.toISO()].current?.scrollIntoView({
        behavior: "smooth",
        block: "center"
      });
    }
  }, [trySelectedSlot?.startTime]);

  const {
    result: slotReservationResult,
    error,
    isLoading,
    execute
  } = useResilientCreateSlotReservation({
    primaryHookProps: {
      contactId: lead.id,
      customerId: customer.id || "",
      centerId: customer.centerId || "",
      serviceId: service?.id || "",
      serviceDurationMinutes: service?.duration || 0
    },
    secondaryHookProps: {
      contactId: lead.id,
      customer: customer as Customer & Defined<Pick<Customer, "id">>
    }
  });

  useEffect(() => {
    if (trySelectedSlot) execute(trySelectedSlot);
  }, [trySelectedSlot]);

  // ON RESERVATION SUCCESS
  useEffect(() => {
    if (!slotReservationResult) return;

    // set the timeslot state
    setSelectedSlot(slotRef.current);
  }, [slotReservationResult, setSelectedSlot]);

  // ON RESERVATION ERROR
  useEffect(() => {
    if (error) {
      // reset the selected timeslot states
      setTrySelectedSlot(undefined);
      slotRef.current = undefined;
      setSelectedSlot(undefined);
    }
  }, [error, setSelectedSlot]);

  const handleClick = (slot: EnhancedOpsSlot) => {
    setTrySelectedSlot(slot);
    slotRef.current = slot;
  };

  const paddedBoundary = DateTime.utc().plus({ minutes: Number(process.env.REACT_APP_TIMESLOT_PADDING_MINUTES || 5) });
  return (
    <Grid container direction="column" overflow="auto" spacing={1} wrap="nowrap" sx={{ pr: 2, height: "100%" }}>
      {timeslots.map((slot, index) => {
        const { startTime, endTime, type } = slot;
        let localDisabled = disabled;

        let selectType =
          selectedSlot?.startTime && startTime.equals(selectedSlot.startTime)
            ? "selected"
            : trySelectedSlot?.startTime && startTime.equals(trySelectedSlot.startTime)
            ? "trySelected"
            : startTime < paddedBoundary
            ? BlockType.Blocked
            : type;

        const isSelected = type === BlockType.Open && selectedSlot?.id === slot.id;
        selectType = isSelected ? "selected" : type;
        localDisabled = !isSelected && type !== BlockType.Open;

        return (
          <Grid item key={index}>
            <Tooltip title={titles[selectType as TimeItemSelectTypes]} placement="right">
              <TimeItem
                selectType={selectType as TimeItemSelectTypes}
                disabled={localDisabled}
                onClick={() => !localDisabled && selectType === BlockType.Open && !isLoading && handleClick(slot)}
                ref={listRefs.current[startTime.toISO()]}
              >
                <Typography variant="body1" align="center">
                  {`${startTime.toLocaleString(DateTime.TIME_SIMPLE)} - ${endTime.toLocaleString(
                    DateTime.TIME_SIMPLE
                  )}`}
                </Typography>
              </TimeItem>
            </Tooltip>
          </Grid>
        );
      })}
    </Grid>
  );
};

interface TimeItemProps extends Partial<CardTypeMap> {
  disabled: boolean;
  selectType: TimeItemSelectTypes;
}

const TimeItem = styled(Card, {
  shouldForwardProp: prop => prop !== "selectType"
})<TimeItemProps>(({ disabled, selectType, theme }) => {
  let style: SxProps<Theme>;
  switch (selectType) {
    case "selected":
      style = {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        cursor: "pointer"
      };
      break;
    case "trySelected":
      style = {
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.contrastText,
        cursor: "not-allowed"
      };
      break;
    case BlockType.Blocked:
      style = {
        backgroundColor: theme.palette.action.disabled,
        color: theme.palette.text.primary,
        cursor: "not-allowed"
      };
      break;
    case BlockType.Booked:
      style = {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.background.paper,
        cursor: "not-allowed"
      };
      break;
    case BlockType.Open:
      style = {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        cursor: "pointer"
      };
      break;
    case BlockType.Reserved:
      style = {
        backgroundColor: theme.palette.secondary.light,
        color: theme.palette.secondary.contrastText,
        cursor: "not-allowed"
      };
      break;
    default:
      throw `Failed to find style for select type ${selectType}.`;
  }
  if (disabled) style = { ...style, opacity: 0.7, cursor: "not-allowed" };
  return { "&": { padding: 0.75, ...style } };
});

export default BookingWizardTimeSelector;
